/**
 * Create Files Utility Functions
 * 
 * Collection of utility functions for converting base64 data and attachment data
 * to File objects throughout the MacSafety web application. These functions are
 * primarily used for preparing attachments and signatures for form submission
 * and API communication.
 * 
 * Key Functions:
 * - base64ToFile: Converts base64 string data to File objects
 * - attachmentDataToFile: Converts AttachmentDataForSubmission to File objects

 * 
 * Primary Use Cases:
 * - Converting cached base64 data back to File objects for API submission
 * - Preparing attachment data for form submission workflows
 * - Converting signature data to File objects for backend processing
 * - Supporting file upload operations that require File objects
 * 
 * Components and Services that utilize these functions:
 * - AttachmentUploadService: For preparing attachments as Files for submission
 * - Form submission workflows: For converting cached data to uploadable files
 * - Any component requiring File object creation from base64 or attachment data
 * 
 * @author MacSafety Development Team
 * @version 1.0
 */

import { AttachmentDataForSubmission } from '../models/forms/attachments/attachment-upload';

/**
 * Converts base64 encoded string data to a File object
 * Useful for converting cached or stored base64 data back to File objects
 * that can be used in form submissions, uploads, or other file operations
 * 
 * @param base64Data - The base64 encoded string data (without data URL prefix)
 * @param fileName - The desired filename for the created File object
 * @param contentType - The MIME type of the file (e.g., 'image/png', 'application/pdf')
 * @returns A new File object containing the decoded data
 */
export function base64ToFile(base64Data: string, fileName: string, contentType: string): File {
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new File([byteArray], fileName, { type: contentType });
  }

/**
 * Converts AttachmentDataForSubmission object to a File object
 * Primary function used by AttachmentUploadService for preparing attachments
 * for form submission. Extracts base64 data, filename, and content type
 * to create a proper File object.
 * 
 * @param attachmentData - The attachment data object containing base64 data, filename, and content type
 * @returns File object ready for upload or form submission
 */
export function attachmentDataToFile(attachmentData: AttachmentDataForSubmission): File {
  return base64ToFile(attachmentData.base64Data, attachmentData.fileName, attachmentData.contentType);
}

